import React from 'react';
import { GitHubCheckRun, GitHubChangedFile } from '../../domain/types';
import { Accordion, Button } from '../../ui/common';

export interface GitHubChecksSectionProps {
  itemId: string;
  checks?: GitHubCheckRun[];
  files?: GitHubChangedFile[];
  isReRunningChecks: boolean;
  totalChecksConfigured?: number;
  onReRunChecks: (e: React.MouseEvent) => void;
  onToggleCheckRun: (itemId: string, checkIndex: number) => void;
}


export const GitHubChecksSection: React.FC<GitHubChecksSectionProps> = ({
  itemId,
  checks = [],
  files = [],
  isReRunningChecks,
  totalChecksConfigured = checks.length,
  onReRunChecks,
  onToggleCheckRun,
}) => {
  return (
    <Accordion title="Checks, despliegue y archivos" defaultOpen={true}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Re-run CI checks banner/button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 10px',
            borderRadius: 8,
            background: 'var(--z-fill-2)',
            border: 'none',
          }}
        >
          <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
            {isReRunningChecks
              ? 'Re-ejecutando pipeline de CI/CD…'
              : `${totalChecksConfigured} checks configurados`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label={isReRunningChecks ? 'Ejecutando…' : 'Re-ejecutar CI checks'}
            onClick={onReRunChecks}
            disabled={isReRunningChecks}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                animation: isReRunningChecks ? 'spin 1s linear infinite' : 'none',
              }}
            >
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
            </svg>
            <span>{isReRunningChecks ? 'Ejecutando…' : 'Re-ejecutar CI checks'}</span>
          </Button>
        </div>

        {/* Checks List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {checks.map((check, kIdx) => {
            const statusVal = check.conclusion || check.status || 'ok';
            const ckTone = statusVal === 'ok' ? 'ok' : statusVal === 'warn' ? 'warn' : 'bad';
            const timeLabel = check.durationFormatted || check.time;
            return (
              <div
                key={check.id || kIdx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (statusVal !== 'ok') {
                    onToggleCheckRun(itemId, kIdx);
                  }
                }}
                style={{
                  borderRadius: 10,
                  background: 'var(--z-plate)',
                  border: 'none',
                  overflow: 'hidden',
                  cursor: statusVal !== 'ok' ? 'pointer' : 'default',
                }}
                title={statusVal !== 'ok' ? 'Haz clic para reintentar este check' : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px' }}>
                  <span
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: '50%',
                      background: isReRunningChecks
                        ? 'var(--z-warn)'
                        : ckTone === 'ok'
                          ? 'var(--z-ok)'
                          : ckTone === 'warn'
                            ? 'var(--z-warn)'
                            : 'var(--z-bad)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 9,
                    }}
                  >
                    {isReRunningChecks ? '~' : ckTone === 'ok' ? '✓' : ckTone === 'warn' ? '!' : '×'}
                  </span>
                  <span
                    style={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--z-text-3)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {check.name}
                  </span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10,
                      color: 'var(--z-faint)',
                    }}
                  >
                    {timeLabel}
                  </span>
                </div>
                <div
                  style={{
                    padding: '0 10px 9px 29px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <span style={{ fontSize: 11.5, color: 'var(--z-muted-3)' }}>{check.title}</span>
                  {(check.summaryLines || []).map((d, dIdx) => (
                    <span
                      key={dIdx}
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10.5,
                        color: 'var(--z-dim)',
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Files */}
        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
            {files.map((f, fIdx) => (
              <div
                key={fIdx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: 'var(--z-plate)',
                }}
              >
                <span
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11,
                    color: 'var(--z-muted-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    direction: 'rtl',
                    textAlign: 'left',
                  }}
                >
                  {f.filename}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10.5,
                    color: 'var(--z-ok-ink)',
                  }}
                >
                  +{f.additions}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10.5,
                    color: 'var(--z-bad-ink)',
                  }}
                >
                  −{f.deletions}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Accordion>
  );
};
